import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Link as Scroll } from "react-scroll";
import { Divide as Hamburger } from "hamburger-react";
import { X, MessageCircle } from "lucide-react";
import { companyDetails, logo } from "../../data/constant";

const links = [
  {
    name: "Home",
    path: "/",
    scroll: false,
  },
  {
    name: "Services",
    path: "services",
    scroll: true,
  },
  {
    name: "Contact Us",
    path: "contact",
    scroll: true,
  },
];

const LandingHeader = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.setAttribute("data-drawer-open", "true");
    } else {
      document.body.style.overflow = "unset";
      document.body.removeAttribute("data-drawer-open");
    }
    return () => {
      document.body.style.overflow = "unset";
      document.body.removeAttribute("data-drawer-open");
    };
  }, [isOpen]);

  return (
    <div
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-md shadow-md py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="wrapper flex items-center justify-between">
        <Scroll
          to="banner"
          spy
          smooth
          duration={1000}
          className="cursor-pointer"
        >
          <img
            src={logo}
            alt="PANTHM AI Labs"
            className="h-10 md:h-12 object-contain"
          />
        </Scroll>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((link) =>
            link.scroll ? (
              <Scroll
                key={link.name}
                to={link.path}
                className={`text-sm font-medium cursor-pointer transition-colors ${
                  scrolled ? "text-slate-700 hover:text-primary" : "text-white hover:text-primary"
                }`}
                spy={true}
                smooth={true}
                offset={-90}
                duration={1000}
                activeClass="!text-primary font-semibold"
              >
                {link.name}
              </Scroll>
            ) : (
              <Link
                key={link.name}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  scrolled ? "text-slate-700 hover:text-primary" : "text-white hover:text-primary"
                }`}
              >
                {link.name}
              </Link>
            )
          )}
          <a
            href={`https://wa.me/${companyDetails.phone.replace(/[^0-9]/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="primary-btn px-6 py-2.5 rounded-full flex items-center gap-2 text-sm"
          >
            Let's Chat <MessageCircle size={18} />
          </a>
        </div>

        <div className="lg:hidden">
          <Hamburger
            color={scrolled ? "#000000" : "#ffffff"}
            size={24}
            toggled={isOpen}
            rounded
            toggle={setIsOpen}
          />
        </div>
      </div>

      {/* Native Slide-Over Drawer for Landing Pages */}
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[99998] transition-opacity duration-300 lg:hidden ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      <div
        className={`fixed top-0 right-0 h-full w-[85%] max-w-[350px] bg-white dark:bg-[#07090e] text-slate-900 dark:text-white z-[99999] shadow-2xl border-l border-slate-200 dark:border-slate-800 transition-transform duration-300 ease-in-out transform lg:hidden flex flex-col pointer-events-auto ${
          isOpen ? "translate-x-0" : "translate-x-full pointer-events-none"
        }`}
        style={{ touchAction: "manipulation" }}
      >
        <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
          <img src={logo} alt="logo" className="h-8 object-contain dark:invert dark:hue-rotate-180" />
          <button onClick={() => setIsOpen(false)} className="p-2 text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 rounded-full transition-colors cursor-pointer" aria-label="Close">
            <X size={22} />
          </button>
        </div>
        <div className="flex flex-col gap-2 flex-1 p-6 overflow-y-auto">
          {links.map((link) =>
            link.scroll ? (
              <Scroll
                onClick={() => setIsOpen(false)}
                key={link.name}
                to={link.path}
                className="text-lg font-bold text-slate-800 dark:text-slate-100 hover:text-primary transition-colors cursor-pointer py-3 border-b border-slate-100 dark:border-slate-800/50"
                spy={true}
                smooth={true}
                offset={-80}
                duration={1000}
              >
                {link.name}
              </Scroll>
            ) : (
              <Link
                onClick={() => setIsOpen(false)}
                key={link.name}
                to={link.path}
                className="text-lg font-bold text-slate-800 dark:text-slate-100 hover:text-primary transition-colors py-3 border-b border-slate-100 dark:border-slate-800/50"
              >
                {link.name}
              </Link>
            )
          )}
        </div>
        <div className="p-6 border-t border-slate-100 dark:border-slate-800 flex-shrink-0">
          <a
            href={`https://wa.me/${companyDetails.phone.replace(/[^0-9]/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="primary-btn w-full justify-center py-3.5 rounded-xl flex items-center gap-2 font-bold"
            onClick={() => setIsOpen(false)}
          >
            Let's Chat <MessageCircle size={18} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default LandingHeader;
